import { useContext, useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';
import router from 'next/router';
import AuthContext from '../../../AppContext/AuthProvider';
import { AUTH_ERRORS, CUSTOM_ERRORS } from '../../../Constants/ErrorMessages';
import useAsyncTask from '../../../hooks/useAsyncTask';
import useAuthActions from '../useAuthActions';
import useErrorHandlers from '../../../hooks/useErrorHandlers';
import { sendEvent } from '../../../hooks/useAnalytics';
import { AuthStateTypeMap, SignUpFormData } from '../../../models/auth';
import { logger, REQUIRE_EMAIL_VERIFICATION } from '../../../utils';
import { getTagList } from '../../../models/Contentful';
import { TSignUpStates } from '../@types';
import { ROUTES } from '../../../Constants/variables';
import ClassroomModel from '../../../models/Classroom';
import helpers from '../../../utils/helpers';

function useSignUpActions() {
  const [state, setState] = useState<TSignUpStates>('sign-up-form');

  const [industries, setIndustries] = useState<string[]>([]);
  const { signUp, login, setAuthState, challengeRedirect } = useContext(AuthContext);
  const { handleError } = useErrorHandlers();

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const industries = await getTagList('industries');
      setIndustries(industries);
    } catch (error) {
      logger.error(error);
    }
  };

  const joinAndGoToClassroom = async (values: any) => {
    if (localStorage.getItem('classCode') !== null && values) {
      await ClassroomModel.joinClassroom({
        class_code: localStorage.getItem('classCode'),
        email: values.email,
      });
      const classroomId = localStorage.getItem('classroomId');
      if (classroomId) {
        localStorage.removeItem('classCode');
        localStorage.removeItem('classroomId');
        router.push(
          helpers.generatePath(ROUTES.JOIN_CLASSROOM, {
            classroomId,
          }),
        );
      }
    }
  };

  const handleSignUp = useAsyncTask<{
    values: Partial<SignUpFormData>;
    formikHelpers: FormikHelpers<Partial<SignUpFormData>>;
  }>(async data => {
    const { values, formikHelpers } = data;
    // Retrieve URL from challenge page
    const challengeUrl = localStorage.getItem('last_url');
    const challengeId = localStorage.getItem('challengeId');
    try {
      await signUp(values);
      if (!REQUIRE_EMAIL_VERIFICATION) {
        // If the user is signing up regularly, they will have a password
        if (values.password) {
          await login({ email: values.email, password: values.password });
          if (challengeUrl && challengeId && challengeRedirect) {
            await challengeRedirect(challengeId, challengeUrl);
            // If user has signed up through a join classroom link
          } else if (localStorage.getItem('classCode') && values) {
            return await joinAndGoToClassroom(values);
          } else if (localStorage.getItem('partnerUserId')) {
            return await useAuthActions.partnerRedirectToActivity(values.email, true);
          } else {
            router.push(ROUTES.DASHBOARD);
          }
        }
        // If the user is signing up using Google SSO, they will have a sso_id
        else if (values.sso_id) {
          await login({
            email: values.email,
            sso_id: values.sso_id,
          });
          if (challengeUrl && challengeId && challengeRedirect) {
            await challengeRedirect(challengeId, challengeUrl);
          } else if (localStorage.getItem('partnerUserId')) {
            return await useAuthActions.partnerRedirectToActivity(values.email, true);
          } else if (!localStorage.getItem('classCode')) {
            router.push(ROUTES.DASHBOARD);
          }
        }
        const signupType = values.sso_id ? 'sso' : 'email';
        sendEvent('sign_up', {
          user_type: values.role,
          sign_up_type: signupType,
          nonInteraction: false,
        });
      } else {
        setState('thank-you');
        window.scroll({ top: 0 });
      }
      return await formikHelpers.resetForm();
    } catch (error: any) {
      setAuthState(AuthStateTypeMap.SSO, null);
      const responseCode = error?.response?.data?.responseCode;
      if (responseCode === AUTH_ERRORS.PERMISSION_DENIED) {
        return formikHelpers.setErrors({
          mentorCode: AUTH_ERRORS.MENTOR_PERMISSION_DENIED,
        });
      }
      if (responseCode === AUTH_ERRORS.USER_VALIDATION_FAILING) {
        return handleError({
          code: AUTH_ERRORS.USER_VALIDATION_FAILING,
        });
      }
      if (responseCode === AUTH_ERRORS.VERIFICATION_TOKEN_NOT_GENERATED) {
        return handleError({
          code: AUTH_ERRORS.VERIFICATION_TOKEN_NOT_GENERATED,
        });
      }
      if (responseCode === CUSTOM_ERRORS.VERIFICATION_MAIL_NOT_SENT_SUCCESSFULLY) {
        return handleError({
          code: CUSTOM_ERRORS.VERIFICATION_MAIL_NOT_SENT_SUCCESSFULLY,
          metaData: values?.email,
        });
      }
      if (
        error?.response?.status === 422 &&
        error?.response?.data?.message === 'The email has already been taken.'
      ) {
        return handleError({
          code: AUTH_ERRORS.EMAIL_ALREADY_EXISTS_CODE,
        });
      }
      return logger.error(error);
    }
  });

  return {
    state,
    handleSignUp,
    getData,
    industries,
  };
}

export default useSignUpActions;
